'use client';

import React from 'react';

import {
  CheckInSettings,
  CheckOutSettings,
  ReservationWithRateDetailsDTO,
  Settings,
} from '@/models';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

import CheckInButton from './components/check-in-button';
import CheckOutButton from '../check-out/check-out-button';
import CheckInDates from './components/check-in-dates';
import { ReservationDetailsCard } from './components/reservation-details-card';
import ReservationList from './components/reservation-list';

import styles from './index.module.scss';
import { Button } from '../ui/components';
import { ChevronLeft } from '../ui/components/icons';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';

interface CheckInProps {
  locale: string;
  company: string;
  guestBookSlug: string;
  settings: Settings;
  reservations: ReservationWithRateDetailsDTO[];
  focusedLayout?: boolean;
  preview?: boolean;
  checkInSettings: CheckInSettings | null;
  checkOutSettings: CheckOutSettings | null;
  guestId?: string;
}

const CheckIn = ({
  locale,
  company,
  guestBookSlug,
  settings,
  reservations,
  focusedLayout = false,
  preview = false,
  checkInSettings,
  checkOutSettings,
  guestId,
}: CheckInProps): React.ReactElement | null => {
  const searchParams = useSearchParams();
  const { isEnabled } = useFeatureFlags();
  const reservationId = searchParams.get('reservationId') || null;
  const intl = useIntl();
  const router = useRouter();
  const { push } = router;
  const selectedReservation = reservations.find(
    (eachReservation) => eachReservation.id === reservationId,
  );
  const primaryColor = settings.whistle_guest_primary_button_color;
  const showReservationList = reservations.length > 1 && !selectedReservation;
  const isSingleReservation = reservations.length === 1;
  const checkInEnabled = !!checkInSettings?.enabled;

  const handleBack = () => {
    push(`/${locale}/${company}/${guestBookSlug}`);
  };

  if (showReservationList) {
    return (
      <article className={styles.container}>
        <ReservationList
          reservations={reservations}
          primaryColor={primaryColor}
          companyTimezone={settings.whistle_guest_company_timezone}
          checkInSettings={checkInSettings}
          settings={settings}
        />
      </article>
    );
  }

  const reservation = selectedReservation || reservations[0];
  const checkInRoute = `/${locale}/${company}/${guestBookSlug}/reservations/${reservation.id}/check-in`;
  const showCheckInButton = !!(
    (checkInEnabled && focusedLayout && !reservation.hasCheckedIn) ||
    (preview && checkInEnabled && focusedLayout)
  );

  return (
    <article>
      {!isSingleReservation && selectedReservation && (
        <Button
          size="2xs"
          borderColor={primaryColor}
          color={primaryColor}
          variant="outline"
          onClick={handleBack}
        >
          <ChevronLeft />
          {intl.formatMessage({
            id: 'common/backButton_label',
            defaultMessage: 'Back',
          })}
        </Button>
      )}

      <CheckInDates
        reservation={reservation}
        settings={settings}
        primaryColor={primaryColor}
        showCheckInButton={!!(!focusedLayout && checkInEnabled)}
        checkInRoute={checkInRoute}
        preview={preview}
        checkInSettings={checkInSettings}
      />

      <CheckOutButton
        locale={locale}
        company={company}
        companyTimezone={settings.whistle_guest_company_timezone}
        guestBookSlug={guestBookSlug}
        reservation={reservation as ReservationWithRateDetailsDTO}
        primaryColor={primaryColor}
        checkOutSettings={checkOutSettings}
        guestId={guestId}
      />

      <ReservationDetailsCard
        reservation={reservation}
        primaryColor={primaryColor}
        companyTimezone={settings.whistle_guest_company_timezone}
        checkInSettings={checkInSettings}
        showEditButton={true}
        settings={settings}
        reservations={reservations}
      />

      {showCheckInButton && (
        <footer className={styles.footer}>
          <CheckInButton
            reservation={reservation}
            settings={settings}
            redirectPath={checkInRoute}
            checkInSettings={checkInSettings}
          />
        </footer>
      )}
    </article>
  );
};

export default CheckIn;
